import type { CSSProperties } from "react";

import { FLIP_KEY_ATTRIBUTE } from "@/lib/motion";

// Loaded here rather than in the barrel, on the same reasoning as
// `overlay/surface.ts`: `enter.ts` is the module that owns the
// `data-busee-enter` contract, so importing the stylesheet here guarantees the
// keyframes exist wherever a variant is actually used, by the barrel path or by
// a direct one. Vite dedupes the import.
import "./motion.css";

/**
 * The ten ways something is allowed to appear.
 *
 * Spread onto the element that is appearing — never onto a wrapper:
 *
 * ```tsx
 * <li {...enterProps("row", { index })} className="…">
 * ```
 *
 * The whole system is one data attribute and, for the two list variants, one
 * custom property. There is no component here on purpose. Every surface in this
 * app is built out of flex and grid parents whose children carry their own
 * layout classes, and a `<Motion>` wrapper would insert a box into the middle of
 * that — which is how a "harmless" animation library ends up changing the
 * spacing of a list.
 *
 * `motion.css` is the spec for what each variant does; this file only names
 * them.
 */
export type EnterVariant =
  /** A whole screen arriving. */
  | "page"
  /** The navigation bar's crossfade. */
  | "nav"
  /** A list row. Staggers. */
  | "row"
  /** A task or workout row that settles from the right. Staggers. */
  | "row-left"
  /** A date or streak chip that reveals in calendar order. Staggers. */
  | "day-chip"
  /** A dashboard widget that reveals in reading order. Staggers. */
  | "widget"
  /** A kanban or dashboard card. Staggers. */
  | "card"
  /** A FLIP-owned card: fades, but never competes for its transform. */
  | "card-fade"
  /** A region whose contents were replaced. */
  | "content"
  /** A loading placeholder. */
  | "skeleton"
  /** A status line, badge or inline error. */
  | "status";

/**
 * What {@link enterProps} spreads onto an element.
 *
 * Deliberately **without** the `data-busee-enter-skip` seal. Suppressing an
 * entrance under a native view transition is not a render-time decision: it is
 * true of an element that has already arrived, forever, and a value recomputed
 * every render is true only until the next one. `lib/motion`'s
 * `sealEnterAnimations` writes it to the DOM directly for that reason — see the
 * note on `ENTER_SKIP_ATTRIBUTE`.
 */
export type EnterProps = {
  "data-busee-enter": EnterVariant;
  style?: CSSProperties;
};

export type EnterOptions = {
  /**
   * Position in the reading order for the variants that stagger.
   * Ignored by the other five.
   *
   * **The cap is not applied here.** `motion.css` clamps it with `min()`
   * against `--busee-stagger-cap`, so the ceiling has one definition and this
   * file carries no copy of a CSS number. Pass the real index; a list of 400
   * rows is as safe as a list of 4.
   */
  index?: number;
  /** Merged, so a call site that already needs an inline style keeps it. */
  style?: CSSProperties;
};

const STAGGERED_VARIANTS = new Set<EnterVariant>([
  "row",
  "row-left",
  "day-chip",
  "widget",
  "card",
  "card-fade",
]);

/**
 * The props that make an element animate in.
 *
 * Returns no `style` at all unless it has something to put in it, so spreading
 * this onto an element that had no inline style does not give it one.
 */
export function enterProps(variant: EnterVariant, options: EnterOptions = {}): EnterProps {
  const { index, style } = options;

  // A non-finite or non-positive index is not an error and is not clamped to
  // something: index 0 is the first row and has no delay, which is the same
  // thing as having no index at all.
  if (
    !STAGGERED_VARIANTS.has(variant) ||
    index === undefined ||
    !Number.isFinite(index) ||
    index <= 0
  ) {
    return style === undefined
      ? { "data-busee-enter": variant }
      : { "data-busee-enter": variant, style };
  }

  return {
    "data-busee-enter": variant,
    style: { ...style, "--busee-enter-index": Math.floor(index) } as CSSProperties,
  };
}

/** What {@link flipProps} spreads onto an element. */
export type FlipProps = { [FLIP_KEY_ATTRIBUTE]: string };

/**
 * Marks an element as one whose *position* should be animated when the list
 * around it reflows — see `useListTransition`.
 *
 * The key has to be the element's identity, not its position: a task id, an
 * issue key, a date. Keying by index means every row after an insertion looks
 * like a different row that happens to be in the same place, and nothing
 * animates.
 */
export function flipProps(key: string): FlipProps {
  return { [FLIP_KEY_ATTRIBUTE]: key };
}
